import { useCallback, useEffect, useRef, useState } from 'react';
import { track } from '../analytics';

export interface TalkResult { blob: Blob | null; seconds: number; mic: boolean }

/**
 * The talk: tap the mic, an opening line, a subject, then feeling words that
 * drift in. The clock starts only once the microphone question is answered.
 * Used by Day 1's warm-up and by any exercise whose input is a recording.
 */
export function Talk({ subject, opening = 'Once upon a time…', prompt, words, seconds, minSeconds, every, onDone, revealAt = 3, diveAt = 26 }: {
  subject: string; opening?: string; prompt?: string; words: string[]; seconds: number; minSeconds: number; every: number; onDone: (r: TalkResult) => void; revealAt?: number; diveAt?: number;
}) {
  const [phase, setPhase] = useState<'asking' | 'talking'>('asking');
  const [elapsed, setElapsed] = useState(0);
  const [word, setWord] = useState<string | null>(null);
  const [mic, setMic] = useState<'unknown' | 'on' | 'off'>('unknown');
  const [level, setLevel] = useState(0);
  const order = useRef<string[]>([]);
  const startedAt = useRef(0);
  const stream = useRef<MediaStream | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const raf = useRef(0);
  const ctxRef = useRef<AudioContext | null>(null);
  const finished = useRef(false);

  const finish = useCallback((why: string) => {
    if (finished.current) return;
    finished.current = true;
    const secs = Math.round((Date.now() - startedAt.current) / 1000);
    cancelAnimationFrame(raf.current);
    ctxRef.current?.close().catch(() => {});
    const done = (blob: Blob | null) => { stream.current?.getTracks().forEach((t) => t.stop()); track('talk_done', { seconds: secs, mic: mic === 'on', why }); onDone({ blob, seconds: secs, mic: mic === 'on' }); };
    const r = recorder.current;
    if (r && r.state !== 'inactive') { r.onstop = () => done(new Blob(chunks.current, { type: r.mimeType || 'audio/webm' })); r.stop(); }
    else done(null);
  }, [mic, onDone]);

  useEffect(() => {
    if (phase !== 'talking') return;
    const id = setInterval(() => {
      const secs = (Date.now() - startedAt.current) / 1000;
      setElapsed(secs);
      const n = Math.floor((secs - diveAt) / every) + 1;
      if (secs >= diveAt && n >= 1 && n - 1 < order.current.length) setWord(order.current[n - 1]);
      if (secs >= seconds) finish('time');
    }, 200);
    return () => clearInterval(id);
  }, [phase, every, seconds, diveAt, finish]);

  useEffect(() => {
    const a = [...words];
    for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
    order.current = a;
    const begin = () => { startedAt.current = Date.now(); setPhase('talking'); };
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.current = s; setMic('on'); chunks.current = [];
        const r = new MediaRecorder(s); r.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); }; r.start(1000); recorder.current = r;
        const ctx = new AudioContext(); ctxRef.current = ctx;
        const an = ctx.createAnalyser(); an.fftSize = 512; ctx.createMediaStreamSource(s).connect(an);
        const buf = new Uint8Array(an.fftSize);
        const tick = () => { an.getByteTimeDomainData(buf); let sum = 0; for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; } setLevel(Math.min(1, Math.sqrt(sum / buf.length) * 4)); raf.current = requestAnimationFrame(tick); };
        tick(); begin();
      } catch { setMic('off'); begin(); }
    })();
    return () => { cancelAnimationFrame(raf.current); stream.current?.getTracks().forEach((t) => t.stop()); ctxRef.current?.close().catch(() => {}); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const R = 88, C = 2 * Math.PI * R;
  const progress = Math.min(1, elapsed / seconds);
  const revealed = elapsed >= revealAt;
  const canStop = elapsed >= minSeconds;

  if (phase === 'asking') {
    return (
      <div className="talk">
        <div className="ring-wrap"><svg className="ring" viewBox="0 0 200 200" width="220" height="220"><circle cx="100" cy="100" r={R} fill="none" stroke="var(--rule)" strokeWidth="10" /></svg><div className="ring-centre"><span className="label">One moment</span></div></div>
        <p className="why" style={{ textAlign: 'center' }}>Your phone is asking about the microphone. Either answer is fine.</p>
      </div>
    );
  }

  return (
    <>
      <div className="talk">
        <div className="ring-wrap">
          <svg className="ring" viewBox="0 0 200 200" width="220" height="220">
            <circle cx="100" cy="100" r={R} fill="none" stroke="var(--rule)" strokeWidth="10" />
            <circle cx="100" cy="100" r={R} fill="none" stroke="var(--green)" strokeWidth="10" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - progress)} transform="rotate(-90 100 100)" />
          </svg>
          <div className="level" style={{ transform: `scale(${1 + level * 0.9})`, opacity: mic === 'on' ? 0.55 : 0.2 }} />
          <div className="ring-centre">
            {word ? <><span className="label">Now make it</span><span key={word} className="word">{word}</span></> : <span className="label">{revealed ? 'Keep going' : 'Say it'}</span>}
          </div>
        </div>
        {/* the opening line never leaves: you are telling a story, and the
            subject drops in under it and stays there too (owner, 2026-09-18) */}
        <div className={`story ${revealed ? 'on' : ''}`}>
          <div className="reveal">{opening}</div>
          {revealed && <div key="subject" className="reveal subject">{subject}</div>}
        </div>
        {/* the invitation to just talk is up from the first second and stays until the first prompt (owner, 2026-09-18) */}
        {!word && <p className="why dive" style={{ textAlign: 'center', fontSize: 18 }}>{prompt || 'Just talk. Dive in. Say it.'}</p>}
        {revealed && word && <p className="hint" style={{ textAlign: 'center' }}>Still {subject.replace(/^…/, '')} Keep talking.</p>}
        {mic === 'off' && <p className="hint" style={{ textAlign: 'center' }}>No microphone, and that is fine. Say it out loud anyway.</p>}
      </div>
      <div className="stack" style={{ marginTop: 'auto' }}>
        <button className="btn" onClick={() => finish('enough')} disabled={!canStop} style={{ background: canStop ? undefined : 'var(--rule)', boxShadow: canStop ? undefined : 'none', color: canStop ? undefined : 'var(--muted)' }}>{canStop ? 'That is enough' : 'A little longer'}</button>
        <div className="row" style={{ justifyContent: 'center' }}><button className="link" onClick={() => finish('skip')}>Skip to the end</button></div>
      </div>
    </>
  );
}
